import { UnstyledButton } from "@mantine/core";
import { useElementSize } from "@mantine/hooks";
import { useEffect, useRef, type CSSProperties, type KeyboardEvent, type PointerEvent } from "react";

import { moved, placement, removed, resized, visible, type Composition, type Frame, type TextLayer } from "./model";
import { drawText, loadTextFont } from "./text";
import classes from "./layer-editor.module.css";

/** Live's room around the document for the corner handles. */
const GUTTER = 24;
/** Live's arrow nudge: 1 document pixel, 10 with Shift. */
const NUDGE = 1;
const SHIFT_NUDGE = 10;
const UNMEASURED_VIEW = 0.5;

type Corner = "nw" | "ne" | "sw" | "se";
const CORNERS: Corner[] = ["nw", "ne", "sw", "se"];

export interface LayerStageProps {
  /** What is shown: the committed composition, or a gesture's while it runs. */
  composition: Composition;
  selected: string | null;
  /** While a save runs: a layer can be selected, not changed, as live's. */
  locked: boolean;
  onSelect: (id: string) => void;
  /** A gesture's composition while the pointer moves; null when it ends or is cancelled. */
  onGesture: (during: Composition | null) => void;
  /** A finished change: one undo step. */
  onCommit: (next: Composition) => void;
}

interface Gesture {
  pointer: number;
  id: string;
  corner: Corner | null;
  startX: number;
  startY: number;
  start: Composition;
  last: Composition;
}

/**
 * Live's canvas (spec G13b): the document fitted in the view with room for the handles, on a
 * checkerboard; each layer drawn in its place, with a button over its frame ("Move {name}");
 * a drag moves it, a corner resizes it along the diagonal, the arrows nudge it, Delete removes
 * it. A gesture is one undo step; a cancelled pointer puts it back.
 */
export function LayerStage(props: LayerStageProps) {
  const { composition, selected, locked, onSelect, onGesture, onCommit } = props;
  const { ref, width: roomWidth, height: roomHeight } = useElementSize();
  const gesture = useRef<Gesture | null>(null);
  // Document pixels to screen pixels: the document fitted in the room left by the gutter (half
  // size until the room is measured).
  const view =
    roomWidth > 2 * GUTTER && roomHeight > 2 * GUTTER
      ? Math.min(
          (roomWidth - 2 * GUTTER) / composition.width,
          (roomHeight - 2 * GUTTER) / composition.height,
        )
      : UNMEASURED_VIEW;

  const begin = (event: PointerEvent<HTMLElement>, id: string, corner: Corner | null) => {
    if (!event.isPrimary || event.button !== 0) return;
    event.stopPropagation();
    onSelect(id);
    if (locked) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    gesture.current = {
      pointer: event.pointerId,
      id,
      corner,
      startX: event.clientX,
      startY: event.clientY,
      start: composition,
      last: composition,
    };
  };
  const move = (event: PointerEvent<HTMLElement>) => {
    const running = gesture.current;
    if (running === null || running.pointer !== event.pointerId) return;
    const dx = (event.clientX - running.startX) / view;
    const dy = (event.clientY - running.startY) / view;
    running.last =
      running.corner === null
        ? moved(running.start, running.id, dx, dy)
        : resized(running.start, running.id, running.corner, dx, dy);
    onGesture(running.last);
  };
  const end = (event: PointerEvent<HTMLElement>) => {
    const running = gesture.current;
    if (running === null || running.pointer !== event.pointerId) return;
    gesture.current = null;
    onGesture(null);
    if (running.last !== running.start) onCommit(running.last);
  };
  const cancel = (event: PointerEvent<HTMLElement>) => {
    if (gesture.current?.pointer !== event.pointerId) return;
    gesture.current = null;
    onGesture(null);
  };
  // The arrows and Delete act on the selected layer, its button focused (choosing a layer in
  // the rail focuses it, G13b decision 7, where live's rail leaves the focus behind).
  const keys = (event: KeyboardEvent<HTMLElement>) => {
    if (selected === null || gesture.current !== null || locked) return;
    const step = event.shiftKey ? SHIFT_NUDGE : NUDGE;
    const arrows: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0],
      ArrowRight: [step, 0],
      ArrowUp: [0, -step],
      ArrowDown: [0, step],
    };
    const arrow = arrows[event.key];
    if (arrow !== undefined) {
      event.preventDefault();
      onCommit(moved(composition, selected, arrow[0], arrow[1]));
    } else if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      onCommit(removed(composition, selected));
    }
  };

  const shown = visible(composition);
  return (
    <div ref={ref} className={classes.room}>
      <section
        className={classes.document}
        style={{ width: composition.width * view, height: composition.height * view }}
        aria-label={`Canvas, ${composition.width} × ${composition.height}`}
      >
        {shown.map((id) => {
          const layer = composition.layers[id]!;
          const place = placement(composition, id);
          const box = {
            left: place.x * view,
            top: place.y * view,
            width: place.width * view,
            height: place.height * view,
          };
          return layer.kind === "picture" ? (
            <img key={`picture-${id}`} src={layer.url} alt="" className={classes.picture} style={box} />
          ) : (
            <TextCanvas key={`text-${id}`} layer={layer} place={place} style={box} />
          );
        })}
        {shown.map((id) => {
          const layer = composition.layers[id]!;
          const place = placement(composition, id);
          const box = {
            left: place.x * view,
            top: place.y * view,
            width: place.width * view,
            height: place.height * view,
          };
          const chosen = id === selected;
          return (
            <UnstyledButton
              key={`button-${id}`}
              className={classes.hit}
              data-selected={chosen || undefined}
              style={box}
              aria-label={`Move ${layer.name}`}
              aria-pressed={chosen}
              data-layer={id}
              onPointerDown={(event) => begin(event, id, null)}
              onPointerMove={move}
              onPointerUp={end}
              onPointerCancel={cancel}
              onKeyDown={keys}
              onFocus={() => onSelect(id)}
            >
              {chosen
                ? CORNERS.map((corner) => (
                    <span
                      key={corner}
                      className={classes.handle}
                      data-corner={corner}
                      aria-hidden
                      onPointerDown={(event) => begin(event, id, corner)}
                      onPointerMove={move}
                      onPointerUp={end}
                      onPointerCancel={cancel}
                    />
                  ))
                : null}
            </UnstyledButton>
          );
        })}
      </section>
    </div>
  );
}

/**
 * A text layer drawn as the saved image draws it (live's `text-layer-canvas`), at document
 * resolution and shown at the view's size. It draws again once its font is in; a font that does
 * not load draws in its category's fallback.
 */
function TextCanvas({ layer, place, style }: { layer: TextLayer; place: Frame; style: CSSProperties }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const width = Math.max(1, Math.ceil(place.width));
  const height = Math.max(1, Math.ceil(place.height));
  useEffect(() => {
    let current = true;
    const draw = () => {
      const context = current ? ref.current?.getContext("2d") : null;
      if (context === null || context === undefined) return;
      context.clearRect(0, 0, width, height);
      drawText(context, layer, { x: 0, y: 0, width, height });
    };
    loadTextFont(layer).then(draw, draw);
    return () => {
      current = false;
    };
  }, [layer, width, height]);
  return <canvas ref={ref} width={width} height={height} className={classes.picture} style={style} aria-hidden />;
}
